import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Loader } from '@app/components';
import {
  checkPhoneNumber,
  signInByService,
  SignInData,
  sendTelegramCode,
} from '@app/api';
import { AxiosError } from 'axios';
import { IconArrowBack, IconCloseArrow } from '@app/icons';
import Logo from './logo.png';
import { useForm } from 'forms/hooks';
import { setMask } from '@app/helpers';
import { AuthType } from '@app/models';
import { useNavigate } from 'react-router-dom';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { Eye, EyeOff } from 'lucide-react';

declare global {
  interface Window {
    grecaptcha: {
      render: (container: string | HTMLElement, parameters: any) => number;
      getResponse: (widgetId?: number) => string;
      reset: (widgetId?: number) => void;
      ready: (callback: () => void) => void;
    };
  }
}

const RECAPTCHA_SITE_KEY = '6Les8tsqAAAAAE1146YpRWz5mcMTWqNj1U-wZzpN';

interface SignInFormProps {
  onSubmit: (data: SignInData) => Promise<boolean>;
  onServiceSignIn: (data: { phone: string; code: string }) => Promise<void>;
  pending: boolean;
  onClickSignUp: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onCurrentStepHandler: (step: number) => void;
  currentStep: number;
  onCancel?: () => void;
}

export interface UserFormData {
  phone: string;
  password: string;
  code: string;
}

const SignInForm: FC<SignInFormProps> = ({
  onSubmit,
  onServiceSignIn,
  pending,
  onClickSignUp,
  onCurrentStepHandler,
  currentStep,
  onCancel,
}) => {
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [disabledBtnNext, setDisabledBtnNext] = useState<boolean>(true);
  const [disabledBtnSignIn, setDisabledBtnSignIn] = useState<boolean>(true);
  const [authType, setAuthType] = useState<AuthType | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const phoneRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const codeRef = useRef<HTMLInputElement | null>(null);
  const [displayPhone, setDisplayPhone] = useState('');
  const captchaRef = useRef<HTMLDivElement>(null);
  const captchaWidgetId = useRef<number | null>(null);
  const [captchaReady, setCaptchaReady] = useState(false);

  const { values, onChange, errors, validate, setErrors } =
    useForm<UserFormData>({
      phone: '',
      password: '',
      code: '',
    });

  const captchaTokenRef = useRef<string | null>(null);
  const navigate = useNavigate();
  const [resendTimer, setResendTimer] = useState(30);
  const resendInterval = useRef<NodeJS.Timeout | null>(null);

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
    captchaTokenRef.current = token;
  };

  const initializeCaptcha = useCallback(() => {
    if (window.grecaptcha && captchaRef.current && !captchaWidgetId.current) {
      if (captchaWidgetId.current !== null) {
        window.grecaptcha.reset(captchaWidgetId.current);
      }

      const widgetId = window.grecaptcha.render(captchaRef.current, {
        sitekey: '6Les8tsqAAAAAE1146YpRWz5mcMTWqNj1U-wZzpN',
        callback: handleCaptchaChange,
      });
      captchaWidgetId.current = widgetId;
    }
  }, []);

  useEffect(() => {
    if (currentStep === 1 && authType === AuthType.PASSWORD) {
      if (window.grecaptcha) {
        window.grecaptcha.ready(initializeCaptcha);
      } else {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          window.grecaptcha.ready(initializeCaptcha);
        };
        document.head.appendChild(script);
      }
    }
  }, [currentStep, authType, initializeCaptcha]);

  useEffect(() => {
    return () => {
      if (captchaWidgetId.current !== null && window.grecaptcha) {
        window.grecaptcha.reset(captchaWidgetId.current);
      }
    };
  }, []);

  const onCheckPhoneNumber = async () => {
    try {
      const response = await checkPhoneNumber({ phone: values.phone });

      if (!response.exists) {
        setErrors({
          phone: 'Пользователь с таким номером не зарегистрирован',
        });
        return;
      }

      setAuthType(response.authType || null);

      // If user has Telegram verified, send code via Telegram
      if (response.isTelegramVerified) {
        await sendTelegramCode({ phone: values.phone });
      }

      onCurrentStepHandler(1);
    } catch (error: any) {
      setErrors({
        phone: error.response?.data?.message || 'Произошла ошибка',
      });
    }
  };

  const onSubmitForm = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (currentStep === 0) return;

      try {
        if (authType === AuthType.PASSWORD) {
          const token = captchaTokenRef.current;
          if (!token) {
            setErrors({ password: 'Подтвердите, что вы не робот' });
            return;
          }

          const data: SignInData = {
            phone: values.phone,
            password: values.password,
          };
          await onSubmit(data);
        } else {
          await onServiceSignIn({
            phone: values.phone,
            code: values.code,
          });
        }
      } catch (e) {
        const error = e as AxiosError<{
          message: string;
        }>;

        if (error.response) {
          const { message } = error.response.data;
          setErrors({
            [authType === AuthType.PASSWORD ? 'password' : 'code']: message,
          });
        }
      }
    },
    [onSubmit, onServiceSignIn, setErrors, values, currentStep, authType]
  );

  const handleServiceRegister = useCallback(() => {
    if (onCancel) onCancel();
    navigate('/register-by-service');
  }, [navigate, onCancel]);

  const handleResendCode = async () => {
    if (resendTimer === 0) {
      try {
        await sendTelegramCode({ phone: values.phone });
        setResendTimer(30);
      } catch (e) {}
    }
  };

  useEffect(() => {
    if (currentStep === 0) {
      phoneRef.current?.focus();
      let value = values.phone || '';
      let formatted = value;
      if (value.length > 0) {
        formatted = '(' + value.substring(0, 3);
      }
      if (value.length >= 4) {
        formatted += ') ' + value.substring(3, 6);
      }
      if (value.length >= 7) {
        formatted += ' ' + value.substring(6, 8);
      }
      if (value.length >= 9) {
        formatted += ' ' + value.substring(8, 10);
      }
      setDisplayPhone(formatted);
    }
    if (currentStep === 1) {
      if (authType === AuthType.PASSWORD) {
        passwordRef.current?.focus();
      } else {
        codeRef.current?.focus();
      }
    }
  }, [currentStep, authType, values.phone]);

  useEffect(() => {
    if (values.phone.length <= 9) {
      setDisabledBtnNext(true);
    } else {
      setDisabledBtnNext(false);
    }

    if (authType === AuthType.PASSWORD) {
      setDisabledBtnSignIn(values.password.length <= 0);
    } else {
      setDisabledBtnSignIn(values.code.length <= 0);
    }

    if (values.phone.length === 0) {
      setErrors({
        phone: '',
      });
    }
  }, [values, authType, setErrors]);

  useEffect(() => {
    if (
      currentStep === 1 &&
      authType !== AuthType.PASSWORD &&
      resendTimer > 0
    ) {
      resendInterval.current = setTimeout(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (resendInterval.current) clearTimeout(resendInterval.current);
    };
  }, [resendTimer, currentStep, authType]);

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="min-h-full p-3 sm:p-6">
        <div className="mb-4 sm:mb-6">
          {currentStep !== 0 && (
            <Button
              variant="outline"
              className="flex items-center mb-4 sm:mb-8 px-2 py-2 sm:py-4 w-auto sm:w-[99px] h-8 sm:h-10 text-primary rounded-lg justify-center !bg-transparent text-sm sm:text-base"
              type="button"
              onClick={() => onCurrentStepHandler(0)}
            >
              <IconArrowBack className="mr-1 w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Назад</span>
            </Button>
          )}
          <div className="flex items-center relative">
            <img
              src={Logo}
              className="w-6 h-8 sm:w-[30px] sm:h-[40px] flex-shrink-0"
              alt="logo"
            />
            <div className="flex flex-col ml-2 sm:ml-1">
              <h2 className="font-medium text-lg sm:text-[21px] leading-[130%] m-0 p-0 text-black">
                SMR
              </h2>
              <p className="font-normal text-xs tracking-[0.25px] text-[#757575] m-0 p-0 leading-tight">
                Поиск подряда и поставщиков
              </p>
            </div>
          </div>
          <div className="my-3 sm:my-4">
            <div className="h-px bg-gray-200 w-full" />
          </div>
        </div>

        <div className="w-full">
          <span className="font-medium text-lg sm:text-2xl text-[#424242] block mb-4 sm:mb-6 leading-tight">
            {currentStep === 1
              ? `${setMask(values.phone, '(###) ### ## ##')}`
              : 'Введите номер'}
          </span>

          <form onSubmit={onSubmitForm}>
            <div className="mb-4 sm:mb-6 flex flex-col gap-2">
              {currentStep === 0 ? (
                <>
                  <label htmlFor="phone" className="text-sm font-medium mb-1">
                    Номер телефона
                  </label>
                  <Input
                    id="phone"
                    type="text"
                    disabled={pending}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      value = value.slice(0, 10);
                      let formatted = value;
                      if (value.length > 0) {
                        formatted = '(' + value.substring(0, 3);
                      }
                      if (value.length >= 4) {
                        formatted += ') ' + value.substring(3, 6);
                      }
                      if (value.length >= 7) {
                        formatted += ' ' + value.substring(6, 8);
                      }
                      if (value.length >= 9) {
                        formatted += ' ' + value.substring(8, 10);
                      }
                      setDisplayPhone(formatted);
                      onChange(value, 'phone');
                    }}
                    value={displayPhone}
                    name="phone"
                    placeholder="(777) 777 77 77"
                    ref={phoneRef}
                    className="mb-2 h-11 sm:h-12 text-sm sm:text-base"
                  />
                  {errors.phone && (
                    <span className="text-red-500 text-xs mt-1">
                      {errors.phone}
                    </span>
                  )}
                </>
              ) : authType === AuthType.PASSWORD ? (
                <>
                  <label
                    htmlFor="password"
                    className="text-sm font-medium mb-1"
                  >
                    Пароль
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      disabled={pending}
                      onChange={(e) => onChange(e.target.value, 'password')}
                      value={values.password}
                      name="password"
                      placeholder="XXXXXXXXXXXX"
                      ref={passwordRef}
                      className="mb-2 pr-12 h-11 sm:h-12 text-sm sm:text-base"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="password-toggle-btn absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center h-8 w-8 rounded-full focus:outline-none focus:ring-0"
                    >
                      {showPassword ? (
                        <EyeOff
                          size={18}
                          className="text-gray-500 sm:w-5 sm:h-5"
                        />
                      ) : (
                        <Eye
                          size={18}
                          className="text-gray-500 sm:w-5 sm:h-5"
                        />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <span className="text-red-500 text-xs mt-1">
                      {errors.password}
                    </span>
                  )}
                  <div className="mt-2 sm:mt-2.5">
                    <div
                      ref={captchaRef}
                      className="w-full max-w-full overflow-hidden"
                    ></div>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-2 text-[#757575] text-sm sm:text-[15px] text-left leading-relaxed">
                    Мы отправили вам код подтверждения.
                    <br />
                    Пожалуйста, введите его ниже.
                  </div>
                  <label htmlFor="code" className="text-sm font-medium mb-1">
                    Код подтверждения
                  </label>
                  <Input
                    id="code"
                    type="text"
                    disabled={pending}
                    onChange={(e) => onChange(e.target.value, 'code')}
                    value={values.code}
                    name="code"
                    placeholder="Введите код"
                    ref={codeRef}
                    className="mb-2 h-11 sm:h-12 text-sm sm:text-base"
                  />
                  {errors.code && (
                    <span className="text-red-500 text-xs mt-1">
                      {errors.code}
                    </span>
                  )}
                  <div className="text-left mt-2">
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={resendTimer > 0}
                      className={`underline p-0 text-sm leading-relaxed break-words ${
                        resendTimer > 0
                          ? 'cursor-not-allowed'
                          : 'cursor-pointer hover:opacity-80'
                      }`}
                      style={{
                        background: 'transparent',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: resendTimer > 0 ? '#aaa' : '#424242',
                      }}
                    >
                      {resendTimer > 0
                        ? `Не получили код? Отправить код ещё раз через ${resendTimer} сек.`
                        : 'Не получили код? Отправить код ещё раз'}
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="mt-6 sm:mt-10 space-y-3 sm:space-y-4">
              {currentStep === 1 && (
                <Button
                  className="w-full h-11 sm:h-13"
                  disabled={disabledBtnSignIn || pending}
                  type="submit"
                >
                  {pending ? <Loader size="small" color="primary" /> : 'Войти'}
                </Button>
              )}

              {currentStep === 0 && (
                <>
                  <Button
                    type="button"
                    onClick={onCheckPhoneNumber}
                    className="w-full h-11 sm:h-13"
                    disabled={disabledBtnNext || pending}
                  >
                    {pending ? (
                      <Loader size="small" color="primary" />
                    ) : (
                      'Продолжить'
                    )}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleServiceRegister}
                    className="w-full h-11 sm:h-13 bg-primary text-white"
                    style={{ background: '#3a57e8', color: '#fff' }}
                  >
                    Зарегистрироваться
                  </Button>
                </>
              )}

              {currentStep === 1 && authType === AuthType.PASSWORD ? (
                <span
                  className="font-medium text-sm sm:text-base text-primary text-center block cursor-pointer hover:underline py-2 break-words"
                  onClick={() => onCurrentStepHandler(2)}
                >
                  Забыли пароль?
                </span>
              ) : null}
              {/* {currentStep === 1 && authType !== AuthType.PASSWORD ? (
                <span
                  className="font-semibold text-base sm:text-lg text-[#1976d2] text-center block cursor-pointer underline py-2 hover:text-[#125ea2] break-words"
                  onClick={onClickSignUp}
                >
                  Зарегистрируйте компанию
                </span>
              ) : null} */}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default memo(SignInForm);
